import type {IntlShape} from 'react-intl';

import type {TeamInviteWithError} from '@mattermost/types/teams';

import {InvitationErrorCode} from './invite_members_form/invitation_error_code';

export class InvitationError {
    static fromInvitation(invite: TeamInviteWithError): InvitationError | null {
        if (invite.error) {
            if (Object.values(InvitationErrorCode).includes(invite.error.id as InvitationErrorCode)) {
                let errorType: InvitationError['type'] = 'error';
                if (invite.error.id === InvitationErrorCode.API_SENDING_ISSUE) {
                    errorType = 'sending_issue';
                }
                if (invite.error.id === InvitationErrorCode.API_LIMIT_EXCEEDED) {
                    errorType = 'limit_exceeded';
                }
                return new InvitationError(
                    invite.error.id as InvitationErrorCode,
                    invite.email,
                    errorType,
                    invite.error.params?.retry_after_seconds,
                    invite.error.params?.remaining_sendings,
                );
            }
            return new InvitationError(invite.error.id as InvitationErrorCode, invite.email, 'unknown');
        }
        if (invite.got_invite_earlier) {
            return new InvitationError(InvitationErrorCode.API_INVITATION_HAS_BEEN_SENT, invite.email, 'warning');
        }
        return null;
    }

    constructor(
        public readonly code: InvitationErrorCode,
        public readonly email: string,
        public readonly type: 'unknown' | 'error' | 'warning' | 'sending_issue' | 'limit_exceeded',
        public readonly retryAfterSeconds?: number,
        public readonly remaininSendings?: number,
        // eslint-disable-next-line no-empty-function
    ) {}

    getLocalizedMessage(intl: IntlShape): string {
        switch (this.code) {
        case InvitationErrorCode.INVALID_EMAIL:
        case InvitationErrorCode.API_INVALID_EMAIL:
        case InvitationErrorCode.API_EMAIL_WRONG_FORMAT:
            return intl.formatMessage({
                id: 'invite.members.error.invalid_email',
                defaultMessage: 'Check email address',
            });
        case InvitationErrorCode.TOO_LONG_EMAIL:
        case InvitationErrorCode.API_EMAIL_WRONG_LENGTH:
            return intl.formatMessage({
                id: 'invite.members.error.too_long_email',
                defaultMessage: 'Too long email',
            });
        case InvitationErrorCode.API_INVALID_CURRENT_TEAM_EMAIL_DOMAIN:
            return intl.formatMessage({
                id: 'invite.members.error.invalid_domain.team',
                defaultMessage: 'You can invite users by mail with a specific domain',
            });
        case InvitationErrorCode.API_INVALID_WORKSPACE_TEAM_EMAIL_DOMAIN:
        case InvitationErrorCode.API_INVALID_WORKSPACE_GUEST_EMAIL_DOMAIN:
            return intl.formatMessage({
                id: 'invite.members.error.invalid_domain.workspace',
                defaultMessage: 'Users can register for a specific domain in the workspace',
            });
        case InvitationErrorCode.API_ALLOWED_GUEST_ROLE_ONLY:
            return intl.formatMessage({
                id: 'invite.members.error.allowed_guest_role_only',
                defaultMessage: 'You can invite this user to the team only with the "Guest" role',
            });
        case InvitationErrorCode.API_ALLOWED_GUEST_PLUS_ROLE_ONLY:
            return intl.formatMessage({
                id: 'invite.members.error.allowed_guest_plus_role_only',
                defaultMessage: 'You can invite this user to the team only with the "Guest +" role',
            });
        case InvitationErrorCode.API_ALLOWED_MEMBER_ROLE_ONLY:
            return intl.formatMessage({
                id: 'invite.members.error.allowed_member_role_only',
                defaultMessage: 'You can invite this user to the team only with the "Member" role',
            });
        case InvitationErrorCode.API_INVALID_TEAM_MEMBER:
        case InvitationErrorCode.API_INVALID_GUEST:
            return intl.formatMessage({
                id: 'invite.members.error.already_in_team',
                defaultMessage: 'User is already in the team',
            });
        case InvitationErrorCode.API_INVITATION_HAS_BEEN_SENT:
            return intl.formatMessage({
                id: 'invite.members.error.invitation_has_been_sent',
                defaultMessage: 'The user has already been invited, the new invitation cancels the previous one',
            });
        case InvitationErrorCode.API_DEACTIVATED_MEMBER:
        case InvitationErrorCode.API_DEACTIVATED_GUEST:
            return intl.formatMessage({
                id: 'invite.members.error.deactivated',
                defaultMessage: 'User deactivated',
            });
        case InvitationErrorCode.API_GUEST_ALREADY_INVITED:
            return intl.formatMessage({
                id: 'invite.members.error.guest_already_invited',
                defaultMessage: 'The user has already been invited to another team with the "Guest" role',
            });
        case InvitationErrorCode.API_GUEST_PLUS_ALREADY_INVITED:
            return intl.formatMessage({
                id: 'invite.members.error.guest_plus_already_invited',
                defaultMessage: 'The user has already been invited to another team with the "Guest +" role',
            });
        case InvitationErrorCode.API_MEMBER_ALREADY_INVITED:
            return intl.formatMessage({
                id: 'invite.members.error.member_already_invited',
                defaultMessage: 'The user has already been invited to another team with the "Member" role',
            });
        case InvitationErrorCode.API_GUEST_TEAMS_LIMIT_EXCEEDED:
            return intl.formatMessage({
                id: 'invite.members.error.guest_teams_limit_exceeded',
                defaultMessage: 'The guest has already been added to the team. User with this role can only be a member of one team',
            });
        case InvitationErrorCode.API_UNAVAILABLE_WORKSPACE:
        case InvitationErrorCode.API_SIGNUP_EMAIL_DISABLED:
            return intl.formatMessage({
                id: 'invite.members.error.invite_members.unavailable_workspace',
                defaultMessage: 'Inviting new users to the team is no longer available in this workspace',
            });
        }

        return '';
    }
}
